import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  TOUR_STEPS,
  markTourDone,
  type StorageLike,
  type TourStep,
} from "@/lib/tour";

// A first-run coach-mark overlay: dimmed backdrop, a spotlight cut around
// the step's target, and a bubble with Back / Next / Skip.
//
// Hand-rolled rather than a tour library BECAUSE the whole behaviour is one
// rect and a counter — a dependency would cost more bundle than it saves,
// on a screen drivers load over cab wifi.

type CoachTourProps = {
  open: boolean;
  onClose: () => void;
  userId?: string | null | undefined;
  storage?: StorageLike | null | undefined;
  // Injectable so a test can drive a one-step or missing-target tour
  // without depending on the real copy.
  steps?: readonly TourStep[];
};

// Gap between the spotlight and the bubble, and how far the spotlight is
// inflated past the target so the element doesn't sit flush to the cutout.
const BUBBLE_GAP = 12;
const SPOTLIGHT_PAD = 8;
const BUBBLE_WIDTH = 300;
const VIEWPORT_MARGIN = 12;

type TargetRect = { top: number; left: number; width: number; height: number };

// Whether the step's element is on this screen, and where. `found` is
// tracked separately from the rect BECAUSE an element can legitimately
// measure 0×0 (jsdom, or a collapsed parent) and that is still not the
// "target missing" case — which falls back to a centered card.
function useTargetRect(selector: string, active: boolean) {
  const [state, setState] = useState<{ found: boolean; rect: TargetRect | null }>({
    found: false,
    rect: null,
  });

  useEffect(() => {
    if (!active) {
      setState({ found: false, rect: null });
      return;
    }
    let raf = 0;
    let last = "";
    const measure = () => {
      const el = document.querySelector(selector);
      const rect = el
        ? (() => {
            const r = el.getBoundingClientRect();
            return { top: r.top, left: r.left, width: r.width, height: r.height };
          })()
        : null;
      const key = rect ? `${rect.top}|${rect.left}|${rect.width}|${rect.height}` : "none";
      // Only on a real change, so the per-frame tick below doesn't
      // re-render the tour sixty times a second while nothing moves.
      if (key !== last) {
        last = key;
        setState({ found: el !== null, rect });
      }
    };

    // Measured every frame rather than only on resize/scroll BECAUSE the
    // thing that actually moves the target is neither of those: the trial
    // banner resolves its query a few hundred ms in and pushes the whole
    // screen down, firing no event at all. Listeners alone left the
    // spotlight ringing the header while the button sat below it.
    const frame = () => {
      measure();
      raf = requestAnimationFrame(frame);
    };
    frame();

    // Backstops. A backgrounded tab freezes rAF entirely, and these cover
    // the ordinary movements directly — including the resize/scroll the
    // spotlight is expected to follow. `true` on scroll: the target may sit
    // in a scrolling ancestor, and scroll doesn't bubble.
    const ro =
      typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [selector, active]);

  return state;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    // jsdom has no matchMedia, and neither do very old mobile browsers.
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mql.matches);
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

export function CoachTour({
  open,
  onClose,
  userId,
  storage,
  steps = TOUR_STEPS,
}: CoachTourProps) {
  const [index, setIndex] = useState(0);
  const reduceMotion = usePrefersReducedMotion();

  // Every open starts at step 1 — a replay from "How it works" shouldn't
  // resume where a skipped first run left off.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const step = steps[index];
  const { found, rect } = useTargetRect(step?.target ?? "", open);

  const isLast = index >= steps.length - 1;

  // Finishing and skipping both mean "don't auto-show this again" — a
  // driver who skips has made their choice as firmly as one who finishes.
  const finish = useCallback(() => {
    markTourDone(userId, storage);
    onClose();
  }, [userId, storage, onClose]);

  const next = useCallback(() => {
    if (isLast) finish();
    else setIndex((i) => i + 1);
  }, [isLast, finish]);

  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish();
      } else if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish, next, back]);

  if (!open || !step) return null;

  const spotlight =
    found && rect
      ? {
          top: rect.top - SPOTLIGHT_PAD,
          left: rect.left - SPOTLIGHT_PAD,
          width: rect.width + SPOTLIGHT_PAD * 2,
          height: rect.height + SPOTLIGHT_PAD * 2,
        }
      : null;

  return (
    <div
      // Backdrop tap = skip, the standard escape hatch on a phone.
      onClick={finish}
      className="fixed inset-0 z-50"
      data-testid="coach-tour"
    >
      {spotlight ? (
        <>
          {/* The dim IS this element's shadow — one box-shadow with a huge
              spread paints everything except the cutout, so there's no
              four-div seam to keep aligned. */}
          <div
            data-testid="coach-tour-spotlight"
            // Deliberately NOT transitioned. The rect is re-measured every
            // frame, and a CSS transition on top/left/width/height fights
            // that: each new value restarts the transition before it can
            // advance, so the cutout freezes at its start value and drifts
            // off the target. The spotlight has to be glued to the element,
            // not chasing it.
            className="absolute rounded-xl pointer-events-none transition-none shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] ring-2 ring-white/70"
            style={{
              top: spotlight.top,
              left: spotlight.left,
              width: spotlight.width,
              height: spotlight.height,
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/60" />
      )}

      <Bubble
        step={step}
        index={index}
        total={steps.length}
        isLast={isLast}
        spotlight={spotlight}
        reduceMotion={reduceMotion}
        onNext={next}
        onBack={back}
        onSkip={finish}
      />
    </div>
  );
}

function Bubble({
  step,
  index,
  total,
  isLast,
  spotlight,
  reduceMotion,
  onNext,
  onBack,
  onSkip,
}: {
  step: TourStep;
  index: number;
  total: number;
  isLast: boolean;
  spotlight: { top: number; left: number; width: number; height: number } | null;
  reduceMotion: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const titleId = `coach-tour-title-${step.id}`;

  // With a target: sit under it, or above when there isn't room below.
  // Without one: dead center, which is the "this step is about a screen
  // you're not on" presentation.
  const style: React.CSSProperties = spotlight
    ? (() => {
        const below = spotlight.top + spotlight.height + BUBBLE_GAP;
        const roomBelow = window.innerHeight - below;
        const placeAbove = roomBelow < 160 && spotlight.top > 180;
        const left = Math.min(
          Math.max(
            spotlight.left + spotlight.width / 2 - BUBBLE_WIDTH / 2,
            VIEWPORT_MARGIN,
          ),
          Math.max(VIEWPORT_MARGIN, window.innerWidth - BUBBLE_WIDTH - VIEWPORT_MARGIN),
        );
        return placeAbove
          ? { position: "absolute", left, bottom: window.innerHeight - spotlight.top + BUBBLE_GAP }
          : { position: "absolute", left, top: below };
      })()
    : {
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      // The bubble sits inside the backdrop, whose click skips the tour.
      onClick={(e) => e.stopPropagation()}
      style={{ ...style, width: BUBBLE_WIDTH, maxWidth: "calc(100vw - 24px)" }}
      // transition-none for the same reason as the spotlight: this element's
      // top/left are recomputed as the target moves, and any transition on
      // them (a stray `duration-*` sets transition-duration while CSS's
      // default transition-property is `all`) pins the bubble at the first
      // position it ever had.
      className="transition-none"
    >
      {/* Motion lives here, on an element that owns no position, so it can
          never interfere with tracking. The one thing reduced-motion
          switches off. */}
      <div
        className={
          "rounded-xl bg-background p-4 shadow-xl border space-y-3" +
          (reduceMotion ? "" : " animate-in fade-in-0")
        }
      >
      <div className="space-y-1">
        <h2 id={titleId} className="text-base font-semibold tracking-tight">
          {step.title}
        </h2>
        <p className="text-sm text-muted-foreground">{step.body}</p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground tabular-nums">
          {index + 1} of {total}
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onSkip}>
            Skip
          </Button>
          {index > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          <Button type="button" size="sm" onClick={onNext} autoFocus>
            {isLast ? "Done" : "Next"}
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}
